import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  User,
  Mail,
  Star,
  Briefcase,
  Loader,
  UserCheck,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit,
  Trash2,
  ShieldCheck,
  ShieldAlert,
  Award,
  X,
} from "lucide-react";
import { adminApi, showSuccess, showError } from "../lib";

interface Mentor {
  _id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  avatar?: string;
  title?: string;
  bio?: string;
  specialty?: string;
  company?: string;
  expertise?: string[];
  pricePerSession?: number;
  rating?: number;
  reviewsCount?: number;
  studentsCount?: number;
  sessionsCount?: number;
  responseTime?: string;
  languages?: string[];
  isEmailVerified?: boolean;
  isTopMentor?: boolean;
  createdAt?: string;
}

const AdminMentorsPage = () => {
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [selectedMentor, setSelectedMentor] = useState<Mentor | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    specialty: "",
    company: "",
    bio: "",
    pricePerSession: 0,
    rating: 0,
    reviewsCount: 0,
    studentsCount: 0,
    sessionsCount: 0,
    responseTime: "",
    languages: "",
    isEmailVerified: false,
    isTopMentor: false,
  });
  const [saving, setSaving] = useState(false);

  const itemsPerPage = 12;

  const fetchMentors = async (page = 1) => {
    try {
      setLoading(true);
      const result = await adminApi.getMentors({
        page,
        limit: itemsPerPage,
        search: searchQuery || undefined,
        verified: undefined,
        topMentor: undefined,
        sortBy: "createdAt",
        sortOrder: "desc",
      });
      if (result.success) {
        setMentors(result.data || []);
        setPagination({
          total: result.pagination?.total || 0,
          pages: result.pagination?.pages || 1,
        });
        setCurrentPage(page);
      }
    } catch (err: any) {
      showError("Failed to load mentors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMentors();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchMentors(1);
  };

  const handleEdit = (mentor: Mentor) => {
    setSelectedMentor(mentor);
    setEditForm({
      specialty: mentor.specialty || "",
      company: mentor.company || "",
      bio: mentor.bio || "",
      pricePerSession: mentor.pricePerSession || 0,
      rating: mentor.rating || 0,
      reviewsCount: mentor.reviewsCount || 0,
      studentsCount: mentor.studentsCount || 0,
      sessionsCount: mentor.sessionsCount || 0,
      responseTime: mentor.responseTime || "",
      languages: Array.isArray(mentor.languages) ? mentor.languages.join(", ") : "",
      isEmailVerified: mentor.isEmailVerified || false,
      isTopMentor: mentor.isTopMentor || false,
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedMentor) return;
    try {
      setSaving(true);
      const res = await adminApi.updateMentor(selectedMentor._id, {
        ...editForm,
        languages: editForm.languages.split(",").map((l) => l.trim()).filter(Boolean),
      });
      if (res.success) {
        showSuccess("Mentor updated successfully");
        setEditModalOpen(false);
        fetchMentors(currentPage);
      } else {
        showError("Failed to update mentor");
      }
    } catch (err) {
      showError("Failed to update mentor");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (mentorId: string) => {
    if (!window.confirm("Are you sure you want to delete this mentor profile?")) return;
    try {
      const res = await adminApi.deleteMentor(mentorId);
      if (res.success) {
        showSuccess("Mentor deleted");
        fetchMentors(currentPage);
      } else {
        showError("Failed to delete mentor");
      }
    } catch (err) {
      showError("Failed to delete mentor");
    }
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg shadow-sm bg-gradient-to-r from-purple-600 to-purple-500">
                <UserCheck className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-bold text-gray-900 md:text-xl">
                Mentor Management
              </h1>
            </div>
            <Link
              to="/dashboard/admin"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
            >
              <ShieldCheck className="w-3 h-3" />
              Admin Dashboard
            </Link>
          </div>
          <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
            Manage mentor profiles, verification, and performance
          </p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="mb-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search mentors by name, specialty, or company..."
                className="w-full py-2 pr-4 text-xs border border-gray-200 rounded-lg pl-9 focus:outline-none focus:border-purple-400"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 text-xs font-medium text-white transition-all rounded-lg bg-gradient-to-r from-purple-600 to-purple-500 hover:shadow-md"
            >
              Search
            </button>
          </div>
        </form>

        {/* Mentors Table */}
        <div className="overflow-hidden bg-white border border-gray-100 shadow-sm rounded-xl">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 text-purple-500 animate-spin" />
            </div>
          ) : mentors.length === 0 ? (
            <div className="py-12 text-center">
              <User className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm text-gray-500">No mentors found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">Mentor</th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">Specialty</th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">Rating</th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">Students</th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">Sessions</th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">Verified</th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">Joined</th>
                      <th className="text-left p-3 text-gray-600 font-medium text-[11px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mentors.map((mentor) => (
                      <tr key={mentor._id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-full shrink-0">
                              {mentor.avatar ? (
                                <img src={mentor.avatar} alt="" className="object-cover w-8 h-8 rounded-full" />
                              ) : (
                                <span className="text-xs font-medium text-purple-700">
                                  {(mentor.firstName?.[0] || mentor.name?.[0] || "M").toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-[11px] font-medium text-gray-900 truncate">
                                {mentor.firstName && mentor.lastName
                                  ? `${mentor.firstName} ${mentor.lastName}`
                                  : mentor.name || "Mentor"}
                              </p>
                              <p className="text-[9px] text-gray-500 truncate">{mentor.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-[11px] text-gray-700">{mentor.specialty || "N/A"}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                            <span className="text-[11px] font-medium text-gray-900">
                              {mentor.rating?.toFixed(1) || "0.0"}
                            </span>
                            <span className="text-[9px] text-gray-500">({mentor.reviewsCount || 0})</span>
                          </div>
                        </td>
                        <td className="p-3 text-[11px] text-gray-700">{mentor.studentsCount || 0}</td>
                        <td className="p-3 text-[11px] text-gray-700">{mentor.sessionsCount || 0}</td>
                        <td className="p-3">
                          {mentor.isEmailVerified ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-green-100 text-green-700">
                              <ShieldCheck className="w-3 h-3" /> Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-yellow-100 text-yellow-700">
                              <ShieldAlert className="w-3 h-3" /> Unverified
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-[11px] text-gray-500">
                          {mentor.createdAt ? new Date(mentor.createdAt).toLocaleDateString() : "N/A"}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleEdit(mentor)}
                              className="p-1.5 hover:bg-purple-50 rounded"
                              title="Edit"
                            >
                              <Edit className="w-3.5 h-3.5 text-purple-600" />
                            </button>
                            <button
                              onClick={() => handleDelete(mentor._id)}
                              className="p-1.5 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100">
                  <button
                    onClick={() => fetchMentors(Math.max(1, safePage - 1))}
                    disabled={safePage <= 1}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-medium rounded-lg border border-gray-200 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-3 h-3" />
                    Prev
                  </button>
                  <span className="text-[10px] text-gray-500">
                    Page {safePage} of {totalPages}
                  </span>
                  <button
                    onClick={() => fetchMentors(Math.min(totalPages, safePage + 1))}
                    disabled={safePage >= totalPages}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-medium rounded-lg border border-gray-200 disabled:opacity-50"
                  >
                    Next
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Edit Modal */}
        {editModalOpen && selectedMentor && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 z-10 flex items-center justify-between p-4 bg-white border-b border-gray-200">
                <h2 className="text-lg font-bold text-gray-900">Edit Mentor</h2>
                <button
                  onClick={() => setEditModalOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full">
                    {selectedMentor.avatar ? (
                      <img src={selectedMentor.avatar} alt="" className="object-cover w-12 h-12 rounded-full" />
                    ) : (
                      <span className="text-lg font-medium text-purple-700">
                        {(selectedMentor.firstName?.[0] || selectedMentor.name?.[0] || "M").toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {selectedMentor.firstName && selectedMentor.lastName
                        ? `${selectedMentor.firstName} ${selectedMentor.lastName}`
                        : selectedMentor.name}
                    </p>
                    <p className="text-[10px] text-gray-500">{selectedMentor.email}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-1">Specialty</label>
                  <input
                    type="text"
                    value={editForm.specialty}
                    onChange={(e) => setEditForm({ ...editForm, specialty: e.target.value })}
                    className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={editForm.company}
                    onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                    className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-1">Bio</label>
                  <textarea
                    value={editForm.bio}
                    onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-medium text-gray-700 mb-1">Price per Session</label>
                    <input
                      type="number"
                      value={editForm.pricePerSession}
                      onChange={(e) => setEditForm({ ...editForm, pricePerSession: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-gray-700 mb-1">Rating</label>
                    <input
                      type="number"
                      step="0.1"
                      value={editForm.rating}
                      onChange={(e) => setEditForm({ ...editForm, rating: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-medium text-gray-700 mb-1">Students Count</label>
                    <input
                      type="number"
                      value={editForm.studentsCount}
                      onChange={(e) => setEditForm({ ...editForm, studentsCount: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-gray-700 mb-1">Sessions Count</label>
                    <input
                      type="number"
                      value={editForm.sessionsCount}
                      onChange={(e) => setEditForm({ ...editForm, sessionsCount: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-1">Languages (comma separated)</label>
                  <input
                    type="text"
                    value={editForm.languages}
                    onChange={(e) => setEditForm({ ...editForm, languages: e.target.value })}
                    className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editForm.isEmailVerified}
                      onChange={(e) => setEditForm({ ...editForm, isEmailVerified: e.target.checked })}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <span className="text-xs text-gray-700">Email Verified</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editForm.isTopMentor}
                      onChange={(e) => setEditForm({ ...editForm, isTopMentor: e.target.checked })}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <span className="text-xs text-gray-700">Top Mentor</span>
                  </label>
                </div>

                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => setEditModalOpen(false)}
                    className="flex-1 px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={saving}
                    className="flex-1 px-4 py-2 text-xs font-medium text-white rounded-lg bg-gradient-to-r from-purple-600 to-purple-500 hover:shadow-md disabled:opacity-60"
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMentorsPage;
